import type { HelpFaq, HelpGuide, HelpSource, HelpTopic, ServiceStatus, SupportAction } from '../../api/help-content';

export interface HelpDocument {
  topics: HelpTopic[];
  faq: HelpFaq[];
  guides: HelpGuide[];
  groups: Array<{ id: string; title: string }>;
  quickQuestions: string[];
  contact: { url: string; label: string } | null;
  agent: boolean;
  dictation: boolean;
  onboarding: HelpGuide[];
}
const text = (value: unknown, max = 4000): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const id = (value: unknown): value is string => text(value, 128) && /^[a-zA-Z0-9_-]+$/.test(value);
const strings = (value: unknown, max: number) =>
  Array.isArray(value) ? (value.slice(0, max).filter(item => text(item, 1000)) as string[]) : [];
export function supportContact(value: unknown) {
  if (!text(value, 200)) return null;
  const address = /^@?[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(value) ? `https://t.me/${value.replace(/^@/, '')}` : value;
  try {
    const url = new URL(address);
    if (
      url.protocol !== 'https:' ||
      !['t.me', 'telegram.me'].includes(url.hostname) ||
      url.username ||
      url.password ||
      url.port ||
      url.search ||
      url.hash ||
      !/^\/[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(url.pathname)
    )
      return null;
    return { url: url.href, label: `@${url.pathname.slice(1)}` };
  } catch {
    return null;
  }
}
/** Опубликованное тело справки не обязано содержать source_state. Проверяем сам контракт. */
export function helpDocument(source: HelpSource | null): HelpDocument | null {
  if (
    !source ||
    (source.source_state && !['available', 'published'].includes(source.source_state)) ||
    source.capabilities?.published_help === false ||
    !text(source.release_version, 64) ||
    !Array.isArray(source.topics) ||
    !Array.isArray(source.faq) ||
    !Array.isArray(source.guides)
  )
    return null;
  const topics = source.topics.slice(0, 32).filter(item => object(item) && id(item.id) && text(item.title, 200));
  const faq = source.faq
    .slice(0, 200)
    .filter(item => object(item) && id(item.id) && id(item.topic) && text(item.question, 500) && text(item.answer, 12000));
  const guides: HelpGuide[] = source.guides
    .slice(0, 200)
    .filter(item => object(item) && id(item.id) && id(item.group) && text(item.title, 200))
    .map(item => ({
      ...item,
      subtitle: text(item.subtitle, 500) ? item.subtitle : undefined,
      price: text(item.price, 256) ? item.price : undefined,
      steps: Array.isArray(item.steps)
        ? item.steps
            .slice(0, 30)
            .filter(
              step => text(step, 2000) || (object(step) && text(step.title, 200) && (step.text === undefined || text(step.text, 2000))),
            )
        : [],
      tips: strings(item.tips, 20),
    }));
  const groups = Array.isArray(source.guide_groups)
    ? source.guide_groups.slice(0, 32).filter(item => object(item) && id(item.id) && text(item.title, 200))
    : [];
  return {
    topics,
    faq,
    guides,
    groups,
    quickQuestions: strings(source.quick_questions, 20),
    contact: supportContact(source.support_contact?.telegram),
    agent: source.capabilities?.support_agent === true,
    dictation: source.capabilities?.support_dictation === true || source.capabilities?.dictation === true,
    onboarding:
      source.onboarding?.available && Array.isArray(source.onboarding.guides)
        ? source.onboarding.guides
            .slice(0, 6)
            .map(key => guides.find(guide => guide.id === key))
            .filter((guide): guide is HelpGuide => !!guide)
        : [],
  };
}
export function matchingHelp(document: HelpDocument, query: string) {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const matches = (value: string) => words.every(word => value.toLocaleLowerCase().includes(word));
  return {
    faq: document.faq.filter(item => matches(`${item.question} ${item.answer}`)),
    guides: document.guides.filter(item =>
      matches(
        `${item.title} ${item.subtitle ?? ''} ${item.steps.map(step => (typeof step === 'string' ? step : `${step.title} ${step.text ?? ''}`)).join(' ')} ${(item.tips ?? []).join(' ')}`,
      ),
    ),
  };
}
export function statusFresh(source: ServiceStatus | null, now = Date.now()) {
  const date = Date.parse(source?.checked_at ?? '');
  return (
    source?.source_state === 'available' &&
    source.capabilities?.live_status === true &&
    Number.isFinite(date) &&
    date <= now + 60_000 &&
    date >= now - 900_000
  );
}
export function openPublishedSupport(contact: { url: string; label: string }, event?: { preventDefault(): void }) {
  const sdk = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void } } }).Telegram?.WebApp;
  if (!supportContact(contact.url) || !sdk?.openTelegramLink) return;
  try {
    sdk.openTelegramLink(contact.url);
    event?.preventDefault();
  } catch {
    /* Обычная ссылка остаётся доступна вне Telegram. */
  }
}
export function isSupportAction(value: unknown): value is SupportAction {
  return object(value) && ['tool', 'collection', 'screen', 'sheet'].includes(String(value.kind)) && id(value.id);
}
